import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { BillingState, ProfileInterface } from "ts-interfaces";
import * as Sentry from "@sentry/react";
import { reportBillingRefusal } from "./billingRefusal";
import { AuthContext } from "../auth/Auth";
import { useLoginState } from "../studioxr/auth/LoginState";
import { useMattoState } from "../MattoState";
import { createCardUpdateSessionAPIUrl } from "./config.js";

// Recovery UI for a failed renewal (issue #3956). The webhook stamps
// profiles.billingState: past_due while Stripe retries (Pro kept). Retries
// exhausted cancels the subscription, which clears the state.

export const isBillingRecoveryState = (state: unknown): state is "past_due" => state === "past_due";

export const billingRecoveryMessage = (_state: BillingState, nextAttemptAt: unknown): string => {
  const next = (nextAttemptAt as { toDate?: () => Date })?.toDate?.() ?? (nextAttemptAt instanceof Date ? nextAttemptAt : null);
  return next
    ? `Your last payment didn't go through. Next attempt ${next.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.`
    : "Your last payment didn't go through. We'll try again in a few days.";
};

// The backend resolves the Stripe customer from the id token; nothing about
// the account is sent from the client.
export const openCardUpdatePortal = async (currentUser: { getIdToken: () => Promise<string> } | null) => {
  if (!currentUser) throw new Error("Not signed in");
  const response = await fetch(createCardUpdateSessionAPIUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${await currentUser.getIdToken()}` },
    body: JSON.stringify({ returnUrl: window.location.href }),
  });
  const body = await response.json().catch(() => null);
  if (!response.ok || !body?.url) {
    throw Object.assign(new Error(body?.error?.message || "Could not open the card update page"), { status: response.status });
  }
  window.location.assign(body.url);
};

const actionSx = { textTransform: "none", fontWeight: 600, fontSize: "13px", padding: "4px 12px", borderRadius: "6px" } as const;
const primarySx = { ...actionSx, background: "#000", color: "#fff", "&:hover": { background: "#222" } } as const;
const secondarySx = { ...actionSx, color: "#000", borderColor: "#000", "&:hover": { borderColor: "#000" } } as const;

// "Stop and cancel" reuses the existing cancel flow: /cancelStripeSubscription
// already cancels past_due immediately.
export const BillingRecoveryActions = () => {
  const { currentUser } = useContext(AuthContext) as { currentUser: { getIdToken: () => Promise<string> } | null };
  const setShowSubscription = useMattoState((state) => state.setShowSubscription);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateCard = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await openCardUpdatePortal(currentUser);
    } catch (err) {
      reportBillingRefusal("createCardUpdateSession", err);
      Sentry.captureException(err);
      setError("We couldn't open the card update page. Please try again or contact studio@mattoboard.com.");
      setBusy(false);
    }
  }, [currentUser]);

  // Stripe retries the updated card itself, so Update card leads.
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button variant="contained" disableElevation onClick={updateCard} disabled={busy} sx={primarySx}>
          {busy ? "Opening…" : "Update card"}
        </Button>
        <Button variant="outlined" onClick={() => setShowSubscription("cancel")} disabled={busy} sx={secondarySx}>
          Stop and cancel
        </Button>
      </Stack>
      {error && <Typography variant="body2" sx={{ color: "#cc010c" }}>{error}</Typography>}
    </Stack>
  );
};

/** The account modal's "Subscription Plan" panel while in dunning. */
export const BillingRecoveryPanel = ({ state, nextAttemptAt }: { state: BillingState; nextAttemptAt?: unknown }) => (
  <Stack direction="column" spacing={1}>
    <Typography variant="h5" component="h5">
      Mattoboard PRO Tier{" "}
      <Typography component="span" variant="h5" sx={{ color: "#cc010c", fontWeight: "bold" }}>
        — payment failed
      </Typography>
    </Typography>
    <Typography sx={{ paddingBottom: "10px" }}>{billingRecoveryMessage(state, nextAttemptAt)}</Typography>
    <BillingRecoveryActions />
  </Stack>
);

/** The profile is a one-time read; one Firestore read per tab focus, at most this often. */
const PROFILE_REFRESH_THROTTLE_MS = 60_000;

/** App-wide banner while profile.billingState is set (mounted in App.jsx). */
export const PastDueBanner = () => {
  const profile = useLoginState((s) => s.profile) as ProfileInterface | null;
  const refreshProfile = useLoginState((s) => s.refreshProfile);
  const state = isBillingRecoveryState(profile?.billingState) ? profile.billingState : null;
  const [dismissed, setDismissed] = useState(false);
  const lastRefreshAt = useRef(0);

  // Re-read the profile when the user comes back to the tab, healthy or not:
  // a renewal that fails while a tab is open must surface, and the webhook
  // clearing the state minutes after the card is fixed must clear the banner.
  // Subscribers only (a Stripe customer): setProfile also re-runs the teams
  // query and analytics identify, which the free tier has no reason to pay.
  useEffect(() => {
    if (!profile?.stripeCustomerId) return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastRefreshAt.current < PROFILE_REFRESH_THROTTLE_MS) return;
      lastRefreshAt.current = now;
      refreshProfile();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [profile?.stripeCustomerId, refreshProfile]);

  if (!profile?.uid || !state || dismissed) return null;
  return (
    <Box role="status" sx={{ position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)", width: { xs: "calc(100% - 16px)", sm: "auto" }, maxWidth: 640, zIndex: 1250 /* below MUI modals (1300) */ }}>
      <Alert severity="warning" icon={false} sx={{ boxShadow: "0 4px 16px rgba(0,0,0,0.18)" }}
        action={<IconButton aria-label="Dismiss" size="small" onClick={() => setDismissed(true)}><CloseIcon fontSize="small" /></IconButton>}>
        <Stack spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{billingRecoveryMessage(state, profile.billingNextAttemptAt)}</Typography>
          <BillingRecoveryActions />
        </Stack>
      </Alert>
    </Box>
  );
};
